import type { FormEvent } from 'react';
import { DatePicker, Field, Input, Modal, ModalActions, cx } from '@app/components/ui';
import { SONG_KEYS, type SongFormData } from './songUtils';

interface SongFormModalProps {
  isOpen: boolean;
  mode: 'add' | 'edit';
  onClose: () => void;
  submitting: boolean;
  formData: SongFormData;
  onChange: (patch: Partial<SongFormData>) => void;
  onSubmit: () => void;
  errors?: Partial<Record<'title' | 'artist', string>>;
}

/** Add a YouTube-link song to the library, or edit one. Same DTO fields as before. */
export const SongFormModal: React.FC<SongFormModalProps> = ({ isOpen, mode, onClose, submitting, formData, onChange, onSubmit, errors = {} }) => {
  const submit = (e: FormEvent) => {
    e.preventDefault();
    onSubmit();
  };
  const today = new Date();

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      title={mode === 'add' ? 'Add a song' : 'Edit song'}
      subtitle={mode === 'add' ? 'Add it with a YouTube link so your team can listen before rehearsal.' : undefined}
    >
      <form onSubmit={submit} noValidate className="flex flex-col gap-4">
        <Field label="Title" htmlFor="song-title" error={errors.title}>
          <Input
            id="song-title"
            data-autofocus
            value={formData.title}
            onChange={(e) => onChange({ title: e.target.value })}
            placeholder="Song title"
            invalid={!!errors.title}
          />
        </Field>
        <div className="grid gap-2.5" style={{ gridTemplateColumns: '2fr 1fr' }}>
          <Field label="Artist" htmlFor="song-artist" error={errors.artist}>
            <Input
              id="song-artist"
              value={formData.artist}
              onChange={(e) => onChange({ artist: e.target.value })}
              placeholder="Artist"
              invalid={!!errors.artist}
            />
          </Field>
          <Field label="BPM" htmlFor="song-bpm">
            <Input
              id="song-bpm"
              mono
              inputMode="numeric"
              value={formData.tempo}
              onChange={(e) => onChange({ tempo: e.target.value.replace(/\D/g, '').slice(0, 3) })}
              placeholder="72"
            />
          </Field>
        </div>
        <div>
          <span className="wp-label" id="song-key-label">
            Key
          </span>
          <div role="radiogroup" aria-labelledby="song-key-label" className="grid grid-cols-6 gap-1.5">
            {SONG_KEYS.map((k) => {
              const on = formData.key === k;
              return (
                <button
                  key={k}
                  type="button"
                  role="radio"
                  aria-checked={on}
                  onClick={() => onChange({ key: k })}
                  className={cx(
                    'wp-press h-10 rounded-[10px] border-0 font-mono font-bold text-[14px] cursor-pointer',
                    on ? 'bg-t1 text-bg' : 'bg-fill text-t2 hover:bg-fill2',
                  )}
                >
                  {k}
                </button>
              );
            })}
          </div>
        </div>
        <Field label="YouTube link" htmlFor="song-youtube" optional>
          <Input
            id="song-youtube"
            type="url"
            value={formData.youtubeUrl}
            onChange={(e) => onChange({ youtubeUrl: e.target.value })}
            placeholder="https://youtube.com/watch?v=…"
          />
        </Field>
        <div className="grid gap-2.5" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(min(180px, 100%), 1fr))' }}>
          <Field label="CCLI number" htmlFor="song-ccli" optional>
            <Input id="song-ccli" mono value={formData.ccliNumber} onChange={(e) => onChange({ ccliNumber: e.target.value })} placeholder="7117726" />
          </Field>
          {mode === 'edit' && (
            <Field label="Last played" htmlFor="song-last">
              <DatePicker
                id="song-last"
                value={formData.lastPlayed ? new Date(`${formData.lastPlayed}T00:00:00`) : null}
                maxDate={today}
                placeholder="Never"
                onChange={(d) =>
                  onChange({ lastPlayed: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}` })
                }
              />
            </Field>
          )}
        </div>
        <ModalActions onCancel={onClose} confirmLabel={mode === 'add' ? 'Add to library' : 'Save changes'} busy={submitting} />
      </form>
    </Modal>
  );
};
